import React from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

/* Composants customisés locaux */
import MultipleGroupeSelect from './MultipleGroupeSelect';
import MultipleStatutSelect from './MultipleStatutSelect';
import MultipleQualificationSelect from './MultipleQualificationSelect';

/* Composants customisés partagés*/
import TypeSelect from 'components/Shared/TypeSelect';
import GroupeSelect from 'components/Shared/GroupeSelect';
import TransporteurSearch from 'components/Shared/TransporteurSearch';
import FournisseurSearch from 'components/Shared/FournisseurSearch';
import ClientSearch from 'components/Shared/ClientSearch';
import ArticleGestionSearch from 'components/Shared/ArticleGestionSearch';
import UtilisateurSearch from 'components/Shared/UtilisateurSearch';
import AgenceAutocomplete from 'components/Shared/AgenceAutocomplete';
import GlowBox from 'components/Shared/GlowBox';

/* Icons */
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DateRangeIcon from '@mui/icons-material/DateRange';

import { ReclamationFiltre } from 'constants.js';

class ReclamationsSearchFilters extends React.Component {

    state = {
        expandedPanels: {
            personnes: false,
            piece: false,
            criteres: false
        }
    };

    componentDidMount() {
        document.addEventListener('keyup', this.handleKeyUp);
    }

    componentWillUnmount() {
        document.removeEventListener('keyup', this.handleKeyUp);
    }

    handleKeyUp = (event) => {
        if (event.key === 'Enter' && this.props.criteres.filtre === ReclamationFiltre.Toutes)
        {
            event.stopPropagation();
            this.props.onClickSearch();
        }
    }

    handlePanelChange = (panel) => (event, isExpanded) => {
        this.setState(prevState => ({
            expandedPanels: {
                ...prevState.expandedPanels,
                [panel]: isExpanded
            }
        }));
    };

    render() {
        const { onChange, onClickSearch, onClickReset, criteres, onChangeClient, onChangeTransporteur, onChangeFournisseur, onChangeArticleGestion,
            onChangeEmetteur, onChangeUtilisateur, onChangeUtilisateurPreneurOrdre, onChangeAgence, onChangeConcerne, onChangeObservateur } = this.props;

        const { expandedPanels } = this.state;

        if (criteres.filtre !== ReclamationFiltre.Toutes) {
            return null;
        }

        return (
                <Card sx={{ overflow: 'visible', marginBottom: '24px' }}>
                    <Stack direction="row" spacing={1} margin={2}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">Recherche de dossiers</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 0 }}>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={onClickReset}
                                startIcon={<RefreshIcon />}
                                sx={{ marginRight: 1 }}
                            >
                                Réinitialiser les critères
                            </Button>

                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={onClickSearch}
                                startIcon={<SearchIcon />}
                            >
                                Rechercher
                            </Button>
                        </Box>
                    </Stack>


                    <CardContent>
                        
                            <Grid container spacing={2} mb={2}>                                    
                                <Grid item xs={2}>                    
                                    <GlowBox actif={!!criteres.dateCreationDebut}>
                                        <TextField
                                            fullWidth
                                            id="dateCreationDebut"
                                            label="Date de début"
                                            type="date"
                                            value={criteres.dateCreationDebut}
                                            onChange={onChange}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            inputProps={{
                                                name: 'dateCreationDebut',
                                                id: 'dateCreationDebut'
                                            }}
                                            size='small'
                                        />
                                    </GlowBox>                
                                </Grid>
                                <Grid item xs={2}>                                                   
                                    <GlowBox actif={!!criteres.dateCreationFin}>
                                        <TextField
                                            fullWidth
                                            id="dateCreationFin"
                                            label="Date de fin"
                                            type="date"
                                            value={criteres.dateCreationFin}
                                            onChange={onChange}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            inputProps={{
                                                name: 'dateCreationFin',
                                                id: 'dateCreationFin'
                                            }}
                                            size='small'
                                        />
                                    </GlowBox>
                                </Grid>
                                <Grid item xs={3}>
                                    <GlowBox actif={criteres.statuts.length !== 0 && criteres.statuts.length !== 5}>
                                        <MultipleStatutSelect 
                                            selected={criteres.statuts} 
                                            onChange={onChange} 
                                            size='small'
                                        />
                                    </GlowBox>
                                </Grid>
                                <Grid item xs={1}>
                                    <GlowBox actif={criteres.typeId}>
                                        <TypeSelect 
                                            value={criteres.typeId} 
                                            onChange={onChange} 
                                            all 
                                            isRequired={false} 
                                            reclamationLiee={false} 
                                            size='small'
                                        />
                                    </GlowBox>
                                </Grid>
                                <Grid item xs={4}>
                                    <GlowBox actif={criteres.qualificationIds.length}>
                                        <MultipleQualificationSelect 
                                            selected={criteres.qualificationIds} 
                                            onChange={onChange} 
                                            all 
                                            isRequired={false} 
                                            size='small'
                                        />
                                    </GlowBox>
                                </Grid>
                            </Grid>


                        <Grid container spacing={2}>   

                            <Grid item xs={5}>
                                {/* Section Personnes concernées */}
                                <GlowBox actif={!expandedPanels.personnes && (
                                        !!criteres.emetteurId
                                        || (criteres.groupesEmetteur && criteres.groupesEmetteur.length > 0)
                                        || !!criteres.codeAgence
                                        || !!criteres.utilisateurId
                                        || !!criteres.groupeId
                                        || !!criteres.observateurId
                                        || !!criteres.utilisateurPreneurOrdreCodePO
                                )}>

                                    <Accordion 
                                        expanded={expandedPanels.personnes} 
                                        onChange={this.handlePanelChange('personnes')}
                                        sx={{ marginBottom: 1 }}
                                        size='small'
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ backgroundColor: 'grey.100' }}
                                        >
                                            <PersonIcon sx={{ marginRight: 1 }} />
                                            <Typography variant="subtitle1">Personnes concernées</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ padding: 1, flexDirection: 'column' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={4}>
                                                    <GlowBox actif={!!criteres.emetteurId}>
                                                        <UtilisateurSearch
                                                            namespace={"searchFiltersEmetteur"}
                                                            onChange={onChangeEmetteur}
                                                            isRequired={false}
                                                            label={"Émetteur"}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <GlowBox actif={criteres.groupesEmetteur && criteres.groupesEmetteur.length > 0}>
                                                        <MultipleGroupeSelect
                                                            selected={criteres.groupesEmetteur}
                                                            onChange={onChange}
                                                            all
                                                            isRequired={false}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <GlowBox actif={!!criteres.codeAgence}>
                                                        <AgenceAutocomplete
                                                            label={"Agence de l'émetteur"}
                                                            value={criteres.codeAgence}
                                                            onChange={onChangeAgence}
                                                            isRequired={false}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                                                            
                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.utilisateurId}>
                                                        <UtilisateurSearch
                                                            namespace={"searchFiltersUtilisateur"}
                                                            onChange={onChangeUtilisateur}
                                                            isRequired={false}
                                                            label={"Utilisateur affecté"}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.groupeId}>
                                                        <GroupeSelect
                                                            label={"Groupe de traitement affecté"}
                                                            value={criteres.groupeId}
                                                            onChange={onChange}
                                                            all
                                                            isRequired={false}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>


                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.observateurId}>
                                                        <UtilisateurSearch
                                                            namespace={"searchFiltersObservateur"}
                                                            onChange={onChangeObservateur}
                                                            isRequired={false}
                                                            label={"Observateur"}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>

                                                {/* Forcer un retour à la ligne */}
                                                <Grid item xs={12} p={0} sx={{height:0, padding:0}}></Grid>

                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.utilisateurPreneurOrdreCodePO}>
                                                        <UtilisateurSearch
                                                            namespace={"searchFiltersUtilisateurPreneurOrdre"}
                                                            onChange={onChangeUtilisateurPreneurOrdre}
                                                            isRequired={false}
                                                            label={"Utilisateur qui a saisi la pièce"}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>

                                </GlowBox>
                            </Grid>


                            <Grid item xs={4}>
                                {/* Section pièce */}
                                <GlowBox actif={!expandedPanels.piece && (
                                    !!criteres.numeroPiece
                                    || !!criteres.codeArticle
                                    || !!criteres.fournisseurCode
                                    || !!criteres.TransporteurId
                                    || !!criteres.departement
                                )}>
                                    <Accordion 
                                        expanded={expandedPanels.piece} 
                                        onChange={this.handlePanelChange('piece')}
                                        sx={{ marginBottom: 1 }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ backgroundColor: 'grey.100' }}
                                        >
                                            <ReceiptIcon sx={{ marginRight: 1 }} />
                                            <Typography variant="subtitle1">Pièce</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ padding: 1, flexDirection: 'column' }}>
                                            <Grid container spacing={2}>

                                                <Grid item xs={12}>
                                                    <GlowBox actif={!!criteres.numeroPiece}>
                                                        <TextField
                                                            fullWidth
                                                            id="numeroPiece"
                                                            label="Numéro de pièce"
                                                            placeholder="Pièce (B/F/P)"
                                                            value={criteres.numeroPiece}
                                                            onChange={onChange}
                                                            inputProps={{
                                                                name: 'numeroPiece',
                                                                id: 'numeroPiece'
                                                            }}
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                        />
                                                    </GlowBox>
                                                </Grid>


                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.codeArticle}>
                                                        <ArticleGestionSearch
                                                            namespace={"searchFilters"}
                                                            onChange={onChangeArticleGestion}
                                                            isRequired={false}
                                                            concerneCheckBox
                                                            concerne={criteres.codeArticleConcerne}
                                                            onChangeConcerne={onChangeConcerne}
                                                            infosLogistique={false}
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.fournisseurCode}>
                                                        <FournisseurSearch
                                                            namespace={"searchFilters"}
                                                            onChange={onChangeFournisseur}
                                                            isRequired={false}
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.TransporteurId}>
                                                        <TransporteurSearch
                                                            namespace={"searchFilters"}
                                                            onChange={onChangeTransporteur}
                                                            isRequired={false}
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <GlowBox actif={!!criteres.departement}>
                                                        <TextField
                                                            fullWidth
                                                            id="departement"
                                                            label="Code département"
                                                            placeholder="ie 72 pour la Sarthe"
                                                            value={criteres.departement}
                                                            onChange={onChange}
                                                            inputProps={{
                                                                name: 'departement',
                                                                id: 'departement'
                                                            }}
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                        />
                                                    </GlowBox>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>

                                </GlowBox>
                            </Grid>


                            <Grid item xs={3}>

                                {/* Section avancés */}
                                <GlowBox actif={!expandedPanels.criteres && (
                                    (!!criteres.client && !!criteres.client.id)
                                    || !!criteres.packingListId
                                    || !!criteres.motCle
                                )}>

                                    <Accordion 
                                        expanded={expandedPanels.criteres} 
                                        onChange={this.handlePanelChange('criteres')}
                                        sx={{ marginBottom: 1 }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ backgroundColor: 'grey.100' }}
                                        >
                                            <DateRangeIcon sx={{ marginRight: 1 }} />
                                            <Typography variant="subtitle1">Critères supplémentaires</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ padding: 1, flexDirection: 'column' }}>
                                            <Grid container spacing={2}>
                                                
                                                <Grid item xs={12}>
                                                    <GlowBox actif={!!criteres.client && !!criteres.client.id}>
                                                        <ClientSearch
                                                            namespace={"searchFilters"}
                                                            onChange={onChangeClient}
                                                            isRequired={false}
                                                            client={criteres.client}
                                                            size='small'
                                                        />
                                                    </GlowBox>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <GlowBox actif={!!criteres.packingListId}>
                                                        <TextField
                                                            fullWidth
                                                            id="packingListId"
                                                            label="Packing list"
                                                            type="number"
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            inputProps={{
                                                                min: 0,
                                                                name: 'packingListId',
                                                                id: 'packingListId'
                                                            }}
                                                            onChange={onChange}
                                                            value={criteres.packingListId}
                                                        />
                                                    </GlowBox>
                                                </Grid>


                                                <Grid item xs={12}>
                                                    <GlowBox actif={!!criteres.motCle}>
                                                        <TextField
                                                            fullWidth
                                                            id="motCle"
                                                            label="Mot clé"
                                                            placeholder="Dans les commentaires de dossiers, sur les lignes ou les détails d'actions."
                                                            value={criteres.motCle}
                                                            onChange={onChange}
                                                            inputProps={{
                                                                name: 'motCle',
                                                                id: 'motCle'
                                                            }}
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                        />
                                                    </GlowBox>
                                                </Grid>

                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </GlowBox>


                            </Grid>
                        </Grid>

                    </CardContent>
                </Card>
        );
    }
}

ReclamationsSearchFilters.propTypes = {
    criteres: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onClickReset: PropTypes.func.isRequired,
    onClickSearch: PropTypes.func.isRequired,
    onChangeClient: PropTypes.func.isRequired,
    onChangeTransporteur: PropTypes.func.isRequired,
    onChangeFournisseur: PropTypes.func.isRequired,
    onChangeArticleGestion: PropTypes.func.isRequired,
    onChangeEmetteur: PropTypes.func.isRequired,
    onChangeUtilisateur: PropTypes.func.isRequired,
    onChangeUtilisateurPreneurOrdre: PropTypes.func.isRequired,
    onChangeAgence: PropTypes.func.isRequired,
    onChangeConcerne: PropTypes.func.isRequired,
    onChangeObservateur: PropTypes.func.isRequired
};

export default ReclamationsSearchFilters;